import styled, { css } from "styled-components";

import { DesignTokens, Popover } from "melco-ui";

import { StatusInfo } from "./item/StatusInfo";
import { WarningBadge } from "./item/WarningBadge";
import { MachineName } from "./item/MachineName";
import { useTranslation } from "react-i18next";

const { spacings, colors, elevations } = DesignTokens;

const MACHINE_STATUS_ITEM_BORDER_RADIUS = 5;

const MACHINE_STATUS_COLOR_DEFAULT = colors.gray.superLight;
const MACHINE_STATUS_COLOR_RUNNING = "#c0e4aa";
const MACHINE_STATUS_COLOR_NOT_CONNECTED = colors.feedback.warning;
const MACHINE_STATUS_COLOR_ERROR = colors.feedback.error;

const Item = styled.div.withConfig({
  shouldForwardProp: (prop) => !["status"].includes(prop),
})<Pick<MachineStatusGridItemProps, "status">>`
  cursor: default;
  display: flex;
  position: relative;
  background-color: ${MACHINE_STATUS_COLOR_DEFAULT};
  border-radius: ${MACHINE_STATUS_ITEM_BORDER_RADIUS}px;
  padding: ${spacings.middle}px;
  min-width: 0; //Prevent from grid blowout

  ${({ status }) => {
    if (status === "running") {
      return css`
        && {
          background-color: ${MACHINE_STATUS_COLOR_RUNNING};
        }
      `;
    }
    if (status === "stopped") {
      return css`
        && {
          background-color: gray;
        }
      `;
    }

    if (status === "notconnected") {
      return css`
        && {
          background-color: ${MACHINE_STATUS_COLOR_NOT_CONNECTED};
          z-index: ${elevations.level1.zIndex};

          &:hover {
            animation-play-state: paused;
          }
        }
      `;
    }

    if (status === "error") {
      return css`
        && {
          background-color: ${MACHINE_STATUS_COLOR_ERROR};
        }
      `;
    }
  }}
`;

type MachineStatusGridItemProps = {
  machineName: string;
  status: "running" | "stopped" | "notconnected" | "error";
  tooltip?: string;
};

export const MachineStatusGridItem: React.FC<MachineStatusGridItemProps> = ({
  machineName,
  status,
  tooltip,
}) => {
  const { t } = useTranslation();
  const item = (
    <Item status={status}>
      <MachineName status={status} name={machineName} />
      <StatusInfo status={status} />
      {status === "notconnected" && <WarningBadge />}
    </Item>
  );

  if (tooltip) {
    return (
      <Popover
        content={tooltip}
        title={t("view.popover.title")}
        visible={true}
        placement="topRight"
      >
        {item}
      </Popover>
    );
  }

  return item;
};
